import { useState } from 'react';
import { Info, Minus, Plus, Tag } from 'lucide-react';
import { ProductImage } from './ProductImage';
import { ProductSpecsDrawer } from './ProductSpecsDrawer';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { formatUsd } from '@/lib/format';
import { displayName, type CatalogItem } from '@/types/domain';

interface ProductCardProps {
  item: CatalogItem;
  unitPrice: number;
  isCustomPrice: boolean;
  /** Customer pricing hasn't resolved yet — show a placeholder, never the list price. */
  pricePending?: boolean;
  qty: number;
  onAdjust: (id: string, delta: number) => void;
  onSet: (id: string, qty: number) => void;
}

export function ProductCard({
  item,
  unitPrice,
  isCustomPrice,
  pricePending = false,
  qty,
  onAdjust,
  onSet,
}: ProductCardProps) {
  const [specsOpen, setSpecsOpen] = useState(false);

  const active = qty > 0;
  // QBO inventory NEVER blocks ordering (Sky, 2026-07-09) — the Add button
  // is always live. Stock badges are informational only, and gases don't
  // even get those: cylinders are filled/swapped to order, so their QBO
  // qty_on_hand (usually 0) is meaningless. Gas is detected by category OR
  // the cylinder sku families so a miscategorized row can't regress this.
  const isGas =
    item.category === 'Gas' || /^(CO|BR|NI)80/i.test(item.sku ?? '');
  const outOfStock = !isGas && item.qtyOnHand === 0;
  const lowStock = !isGas && item.qtyOnHand > 0 && item.qtyOnHand < 25;
  const name = displayName(item);

  return (
    <>
      <div
        className={cn(
          'group relative rounded-2xl bg-brix-surface p-4 transition-all duration-200',
          active
            ? 'border-2 border-brix-primary shadow-card-hover -translate-y-0.5'
            : 'border border-brix-border shadow-card hover:shadow-card-hover hover:-translate-y-0.5',
        )}
      >
        <div className="flex items-start gap-3 mb-3">
          <ProductImage
            imageUrl={item.imageUrl}
            accent={item.accentColor ?? '#6A7780'}
            category={item.category}
            alt={name}
            size="lg"
          />
          <div className="flex-1 min-w-0">
            <div className="flex items-start gap-1.5">
              <p className="font-display text-base font-semibold leading-snug text-brix-ink">
                {name}
              </p>
              {item.tag === 'Bestseller' && (
                <span
                  className="text-[9px] font-bold px-1.5 py-0.5 rounded mt-0.5 shrink-0 bg-brix-accent text-brix-primary"
                  aria-label="Bestseller"
                >
                  ★
                </span>
              )}
            </div>
            <p className="text-xs mt-1 text-brix-muted">{item.category}</p>
            {item.sku && (
              <p className="text-[10px] mt-0.5 font-mono text-brix-muted">{item.sku}</p>
            )}
          </div>
        </div>

        <div className="flex items-end justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-baseline gap-1.5 flex-wrap">
              {pricePending ? (
                <span
                  aria-label="Loading your price"
                  className="inline-block h-6 w-16 rounded bg-brix-border/70 animate-pulse"
                />
              ) : (
                <p className="font-display text-xl font-semibold font-mono text-brix-ink">
                  {formatUsd(unitPrice)}
                </p>
              )}
              {/* Only strike the list price when it is genuinely HIGHER —
                  a struck-out number reads as "you're saving this much", so
                  showing it on a contract price ABOVE list claims a discount
                  the customer is not getting. The "Your price" tag below still
                  shows either way: that one is true regardless of direction. */}
              {!pricePending && isCustomPrice && unitPrice < item.listPrice && (
                <p className="text-[10px] font-mono line-through text-brix-muted">
                  {formatUsd(item.listPrice)}
                </p>
              )}
            </div>
            <div className="flex items-center gap-1.5 mt-1 flex-wrap">
              {!pricePending && isCustomPrice && (
                <span className="text-[9px] font-bold px-1.5 py-0.5 rounded inline-flex items-center gap-0.5 bg-brix-primary-tint text-brix-primary">
                  <Tag size={8} /> Your price
                </span>
              )}
              {outOfStock ? (
                <span className="text-[10px] font-semibold text-brix-warning">
                  Backordered — we&rsquo;ll confirm timing
                </span>
              ) : lowStock ? (
                <span className="text-[10px] text-brix-warning">Low ({item.qtyOnHand})</span>
              ) : null}
            </div>
          </div>

          {qty === 0 ? (
            <Button
              type="button"
              size="sm"
              onClick={() => onAdjust(item.id, 1)}
              className="shrink-0"
            >
              <Plus size={13} /> Add
            </Button>
          ) : (
            <div className="flex items-center gap-1 shrink-0">
              <button
                type="button"
                onClick={() => onAdjust(item.id, -1)}
                aria-label="Decrease quantity"
                className="w-7 h-7 rounded-md border border-brix-primary text-brix-primary flex items-center justify-center hover:bg-brix-primary-tint cursor-pointer transition-colors duration-200"
              >
                <Minus size={12} />
              </button>
              <input
                type="number"
                min={0}
                value={qty}
                onChange={(e) => onSet(item.id, parseInt(e.target.value || '0', 10))}
                className="w-10 text-center font-mono font-bold text-sm outline-none border border-brix-border rounded-md py-1 focus-visible:border-brix-primary"
                aria-label={`${name} quantity`}
              />
              <button
                type="button"
                onClick={() => onAdjust(item.id, 1)}
                aria-label="Increase quantity"
                className="w-7 h-7 rounded-md bg-brix-primary text-white flex items-center justify-center hover:opacity-90 active:scale-[0.96] cursor-pointer transition-all duration-200"
              >
                <Plus size={12} />
              </button>
            </div>
          )}
        </div>

        {/* Specs link — opens modal with BIB photo, full specs, finished volume */}
        <button
          type="button"
          onClick={() => setSpecsOpen(true)}
          className="mt-3 text-[11px] font-semibold text-brix-primary hover:underline inline-flex items-center gap-1 cursor-pointer"
        >
          <Info size={11} /> Specs
        </button>
      </div>

      {specsOpen && (
        <ProductSpecsDrawer
          item={item}
          unitPrice={unitPrice}
          onClose={() => setSpecsOpen(false)}
        />
      )}
    </>
  );
}
